import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";

// Async thunks
export const fetchQuestions = createAsyncThunk(
  "question/fetchQuestions",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get("/admin/questions", { params });
      const data = response.data;

      // Backend returns { success: true, data: [...], pagination: {...} }
      return {
        questions: Array.isArray(data.data) ? data.data : [],
        pagination: data.pagination || {
          page: 1,
          limit: 10,
          total: 0,
          total_pages: 1,
        },
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch questions"
      );
    }
  }
);

export const fetchQuestionById = createAsyncThunk(
  "question/fetchQuestionById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/admin/questions/${id}`);
      const data = response.data;
      return data.data || data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch question"
      );
    }
  }
);

export const createQuestion = createAsyncThunk(
  "question/createQuestion",
  async (questionData, { rejectWithValue }) => {
    try {
      const response = await api.post("/admin/questions", questionData);
      const data = response.data;
      return data.data || data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create question"
      );
    }
  }
);

export const updateQuestion = createAsyncThunk(
  "question/updateQuestion",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/admin/questions/${id}`, data);
      const responseData = response.data;
      return responseData.data || responseData;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update question"
      );
    }
  }
);

export const deleteQuestion = createAsyncThunk(
  "question/deleteQuestion",
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/admin/questions/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete question"
      );
    }
  }
);

export const fetchQuestionUsage = createAsyncThunk(
  "question/fetchQuestionUsage",
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/admin/questions/${id}/usage`);
      return response.data.data || response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch question usage"
      );
    }
  }
);

export const fetchQuestionAnalytics = createAsyncThunk(
  "question/fetchQuestionAnalytics",
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/admin/questions/${id}/analytics`);
      return response.data.data || response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch question analytics"
      );
    }
  }
);

export const bulkImportQuestions = createAsyncThunk(
  "question/bulkImportQuestions",
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post("/admin/questions/bulk-import", data);
      return response.data.data || response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to import questions"
      );
    }
  }
);

const questionSlice = createSlice({
  name: "question",
  initialState: {
    questions: [],
    currentQuestion: null,
    questionUsage: null,
    questionAnalytics: null,
    pagination: {
      page: 1,
      limit: 20,
      total: 0,
      total_pages: 1,
    },
    filters: {
      difficulty: null,
      question_type: null,
      category_id: null,
      search: "",
      page: 1,
    },
    loading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearCurrentQuestion: (state) => {
      state.currentQuestion = null;
    },
    clearQuestionUsage: (state) => {
      state.questionUsage = null;
    },
    clearQuestionAnalytics: (state) => {
      state.questionAnalytics = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch questions
      .addCase(fetchQuestions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchQuestions.fulfilled, (state, action) => {
        state.loading = false;
        state.questions = Array.isArray(action.payload.questions)
          ? action.payload.questions
          : [];
        state.pagination = action.payload.pagination || state.pagination;
      })
      .addCase(fetchQuestions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch question by ID
      .addCase(fetchQuestionById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchQuestionById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentQuestion = action.payload;
      })
      .addCase(fetchQuestionById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create question
      .addCase(createQuestion.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createQuestion.fulfilled, (state, action) => {
        state.loading = false;
        state.questions.unshift(action.payload);
      })
      .addCase(createQuestion.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update question
      .addCase(updateQuestion.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateQuestion.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload || {};
        const index = state.questions.findIndex((q) => q.id === updated.id);
        if (index !== -1) {
          // Merge so list fields (tags, category_id) never blank out if API omits them
          state.questions[index] = {
            ...state.questions[index],
            ...updated,
            tags: updated.tags ?? state.questions[index].tags,
            category_id:
              updated.category_id ?? state.questions[index].category_id,
          };
        }
        state.currentQuestion = updated;
      })
      .addCase(updateQuestion.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete question
      .addCase(deleteQuestion.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteQuestion.fulfilled, (state, action) => {
        state.loading = false;
        state.questions = state.questions.filter(
          (q) => q.id !== action.payload
        );
      })
      .addCase(deleteQuestion.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch question usage
      .addCase(fetchQuestionUsage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchQuestionUsage.fulfilled, (state, action) => {
        state.loading = false;
        state.questionUsage = action.payload;
      })
      .addCase(fetchQuestionUsage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch question analytics
      .addCase(fetchQuestionAnalytics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchQuestionAnalytics.fulfilled, (state, action) => {
        state.loading = false;
        state.questionAnalytics = action.payload;
      })
      .addCase(fetchQuestionAnalytics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Bulk import
      .addCase(bulkImportQuestions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkImportQuestions.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(bulkImportQuestions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  setFilters,
  clearCurrentQuestion,
  clearQuestionUsage,
  clearQuestionAnalytics,
} = questionSlice.actions;
export default questionSlice.reducer;
